import { getCabLandingTripTypeFromCode } from '../../constants/tripTypes';
import {
  fetchLatestCabsLandingStatesInLS,
  saveCabsLandingStatesInLS,
  saveStateInLs
} from '../../utils/localStorageUtils';
import { trackCabsLandingPageEvent } from '../../utils/omnitureTracking';
import { currentTime, nextDayPlusOne, nextDay } from '../../utils/timeUtils';
import { CityLocation, Package } from '../runtime-typechecker';

export const SET_AIRPORT = 'SET_AIRPORT';
export const SET_AIRPORT_CITY = 'SET_AIRPORT_CITY';
export const SET_TRAVELFOR = 'SET_TRAVELFOR';
export const SET_WIDGET = 'SET_WIDGET';
export const SET_SOURCE_CITY = 'SET_SOURCE_CITY';
export const SET_DESTINATION_CITY = 'SET_DESTINATION_CITY';
export const SET_RENTAL_CITY = 'SET_RENTAL_CITY';
export const SET_DEPT_DATE = 'SET_DEPT_DATE';
export const SET_RETURN_DATE = 'SET_RETURN_DATE';
export const SET_HR_PACKAGE = 'SET_HR_PACKAGE';
export const SET_AIRPORT_RIDE_NOW = 'SET_AIRPORT_RIDE_NOW';
export const SET_STOPOVERS = 'SET_STOPOVERS';
export const SET_ACTIVE_STOPOVER = 'SET_ACTIVE_STOPOVER';
export const SET_ERROR_MESSAGE = 'SET_ERROR_MESSAGE';
export const SET_INIT_STATE = 'SET_INIT_STATE';
export const SET_DROP_WIDGET_OPEN = 'SET_DROP_WIDGET_OPEN';

export const AIRPORT = 'airport';
export const SOURCE_CITY = 'sourceCity';
export const DESTINATION_CITY = 'destinationCity';
export const RENTAL_CITY = 'rentalCity';
export const AIRPORT_CITY = 'airportCity';
export const TRAVEL_FOR = 'travelFor';
export const WIDGET = 'widget';
export const DEPT_DATE = 'selectedDeptDate';
export const RETURN_DATE = 'selectedReturnDate';
export const SELECTED_HR_PACKAGE = 'selectedHrPackage';
export const AIRPORT_RIDENOW = 'airportRideNow';
export const STOP_OVERS = 'stopOvers';
export const ACTIVE_STOP_OVER = 'activeStopOver';
export const ERROR_MESSAGE = 'errorMessage';
export const DROP_WIDGET_OPEN = 'dropWidgetOpen';

export const stateNamesForSeparateContextStorage = [
  AIRPORT,
  SOURCE_CITY,
  DESTINATION_CITY,
  RENTAL_CITY,
  AIRPORT_CITY
];

export const stateNamesForWholeContextStorage = [
  AIRPORT,
  SOURCE_CITY,
  DESTINATION_CITY,
  RENTAL_CITY,
  AIRPORT_CITY,
  DEPT_DATE,
  RETURN_DATE
];

export type CabsLandingStatesType = {
  airport: null | CityLocation;
  airportCity: null | CityLocation;
  travelFor: string;
  widget: null | number;
  sourceCity: null | CityLocation;
  destinationCity: null | CityLocation;
  rentalCity: null | CityLocation;
  selectedDeptDate: Date;
  selectedReturnDate: Date;
  selectedHrPackage: null | Package;
  airportRideNow: boolean;
  stopOvers: CityLocation[];
  activeStopOver: null | number;
  errorMessage: string | null;
  packages: Package[];
  dropWidgetOpen: boolean;
  trackTemplateEvent: (eventWithValToTrack: string) => void;
};

const getProcessedPickupAndDropTime = (savedState: CabsLandingStatesType) => {
  let savedPickupTime = new Date(savedState.selectedDeptDate);
  let savedDropTime = new Date(savedState.selectedReturnDate);
  if (!savedPickupTime || savedPickupTime < currentTime()) {
    savedPickupTime = nextDay();
  }
  if (
    !savedDropTime ||
    savedDropTime < currentTime() ||
    savedDropTime < savedPickupTime
  ) {
    savedDropTime = nextDayPlusOne();
  }
  return [savedPickupTime, savedDropTime];
};
export const getInitialState = ({
  selectedTripType,
  useLS = false,
  packages
}: {
  selectedTripType?: string;
  useLS?: boolean;
  packages?: Package[];
}): CabsLandingStatesType => {
  if (useLS) {
    const savedState = fetchLatestCabsLandingStatesInLS();
    if (savedState) {
      savedState.trackTemplateEvent = trackCabsLandingPageEvent;
      savedState.widget = null;
      savedState.travelFor = getCabLandingTripTypeFromCode(selectedTripType);
      savedState.stopOvers = [];
      savedState.activeStopOver = null;
      savedState.packages = packages ? packages : [];
      savedState.selectedHrPackage =
        Array.isArray(packages) && packages.length > 0 ? packages[0] : null;
      savedState.dropWidgetOpen = false;
      const [savedPickupTime, savedDropTime] = getProcessedPickupAndDropTime(
        savedState
      );
      savedState.selectedDeptDate = savedPickupTime;
      savedState.selectedReturnDate = savedDropTime;
      return savedState;
    }
  }
  return {
    airport: null,
    airportCity: null,
    travelFor: getCabLandingTripTypeFromCode(selectedTripType),
    widget: null,
    sourceCity: null,
    destinationCity: null,
    rentalCity: null,
    selectedDeptDate: nextDay(),
    selectedReturnDate: nextDayPlusOne(),
    selectedHrPackage: packages && packages[0] ? packages[0] : null,
    airportRideNow: false,
    stopOvers: [],
    activeStopOver: null,
    errorMessage: null,
    packages: packages || [],
    dropWidgetOpen: false,
    trackTemplateEvent: trackCabsLandingPageEvent
  };
};

export const getUpdatedState = (oldState, newStateValue, newStateKey) => {
  const newStates = {
    ...oldState,
    [newStateKey]: newStateValue
  };
  // save whole states for context
  if (stateNamesForWholeContextStorage.includes(newStateKey)) {
    saveCabsLandingStatesInLS(newStates);
  }

  // save location states for recent searches
  if (stateNamesForSeparateContextStorage.includes(newStateKey)) {
    saveStateInLs({ stateName: newStateKey, stateValue: newStateValue });
  }

  return newStates;
};

export const reducer = (state, action) => {
  const { type, newStateValue, selectedTripType, useLS } = action;
  switch (type) {
    case SET_AIRPORT:
      return getUpdatedState(state, newStateValue, AIRPORT);
    case SET_AIRPORT_CITY:
      return getUpdatedState(state, newStateValue, AIRPORT_CITY);
    case SET_TRAVELFOR:
      return getUpdatedState(state, newStateValue, TRAVEL_FOR);
    case SET_WIDGET:
      return getUpdatedState(state, newStateValue, WIDGET);
    case SET_SOURCE_CITY:
      return getUpdatedState(state, newStateValue, SOURCE_CITY);
    case SET_DESTINATION_CITY:
      return getUpdatedState(state, newStateValue, DESTINATION_CITY);
    case SET_RENTAL_CITY:
      return getUpdatedState(state, newStateValue, RENTAL_CITY);
    case SET_DEPT_DATE:
      return getUpdatedState(state, newStateValue, DEPT_DATE);
    case SET_RETURN_DATE:
      return getUpdatedState(state, newStateValue, RETURN_DATE);
    case SET_HR_PACKAGE:
      return getUpdatedState(state, newStateValue, SELECTED_HR_PACKAGE);
    case SET_AIRPORT_RIDE_NOW:
      return getUpdatedState(state, newStateValue, AIRPORT_RIDENOW);
    case SET_STOPOVERS:
      return getUpdatedState(state, newStateValue, STOP_OVERS);
    case SET_ACTIVE_STOPOVER:
      return getUpdatedState(state, newStateValue, ACTIVE_STOP_OVER);
    case SET_ERROR_MESSAGE:
      return getUpdatedState(state, newStateValue, ERROR_MESSAGE);
    case SET_DROP_WIDGET_OPEN:
      return getUpdatedState(state, newStateValue, DROP_WIDGET_OPEN);
    default:
      return getInitialState({ selectedTripType, useLS });
  }
};
